import React, {useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

const Filter = ({filterOptions, selectedFilters, onToggle}) => {
    const [openKey, setOpenKey] = useState(null)
    const filterRef = useRef(null)
    const buttonRefs = useRef({})
    const [dropdownPos, setDropdownPos] = useState({})
    const [isMobile, setIsMobile] = useState(false)
    const dropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!filterRef.current) return
            if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
                return
            }
            if (!filterRef.current.contains(event.target)) {
                setOpenKey(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        const updateMatch = () => setIsMobile(window.innerWidth <= 768)
        updateMatch()
        window.addEventListener('resize', updateMatch)
        return () => window.removeEventListener('resize', updateMatch)
    }, [])

    useEffect(() => {
        if (!isMobile || !openKey) return
        const updatePosition = () => {
            const button = buttonRefs.current[openKey]
            if (!button) return
            const rect = button.getBoundingClientRect()
            const dropdownWidth = 260
            const left = Math.min(rect.left, window.innerWidth - dropdownWidth - 12)
            setDropdownPos({
                top: rect.bottom + 8,
                left: Math.max(12, left),
                width: dropdownWidth
            })
        }

        updatePosition()
        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('scroll', updatePosition, true)
            window.removeEventListener('resize', updatePosition)
        }
    }, [isMobile, openKey])

    const handleToggle = (key) => {
        if (openKey === key) {
            setOpenKey(null)
            return
        }

        if (isMobile && buttonRefs.current[key]) {
            const rect = buttonRefs.current[key].getBoundingClientRect()
            const dropdownWidth = 260
            const left = Math.min(rect.left, window.innerWidth - dropdownWidth - 12)
            setDropdownPos({
                top: rect.bottom + 8,
                left: Math.max(12, left),
                width: dropdownWidth
            })
        }

        setOpenKey(key)
    }

    const filters = useMemo(() => [
        {
            key: 'type',
            label: 'Type voertuig',
            accent: true,
            options: filterOptions.type || []
        },
        {
            key: 'transmission',
            label: 'Transmissie',
            options: filterOptions.transmission || []
        },
        {
            key: 'fuel',
            label: 'Brandstof',
            options: filterOptions.fuel || []
        },
        {
            key: 'seats',
            label: 'Aantal personen',
            options: filterOptions.seats || []
        },
        {
            key: 'doors',
            label: 'Aantal deuren',
            options: filterOptions.doors || []
        }
    ], [filterOptions])
    const visibleFilters = useMemo(
        () => filters.filter((filter) => filter.options.length > 1),
        [filters]
    )

    useEffect(() => {
        if (openKey && !visibleFilters.some((filter) => filter.key === openKey)) {
            setOpenKey(null)
        }
    }, [openKey, visibleFilters])

    if (visibleFilters.length === 0) return null

    const getSelectedCount = (key) => {
        const selected = selectedFilters?.[key]
        if (!selected) return 0
        if (selected instanceof Set) return selected.size
        if (Array.isArray(selected)) return selected.length
        return 0
    }

    const getFilterLabel = (filter) => {
        const totalCount = filter.options.length
        if (totalCount < 2) return filter.label

        const selectedCount = getSelectedCount(filter.key)
        if (selectedCount > 0) {
            return `${filter.label} (${selectedCount}/${totalCount})`
        }

        return `${filter.label} (${totalCount})`
    }

    return (
        <div className="front-filter" ref={filterRef}>
            <div className="front-filter__row">
                {visibleFilters.map((filter) => (
                    <div className="front-filter__item" key={filter.key}>
                        <button
                            type="button"
                            className={`front-filter__button ${filter.accent ? 'is-accent' : ''}`}
                            ref={(node) => {
                                if (node) {
                                    buttonRefs.current[filter.key] = node
                                }
                            }}
                            onClick={() => handleToggle(filter.key)}
                        >
                            {getFilterLabel(filter)}
                            <i className="bi bi-chevron-down"></i>
                        </button>
                        {openKey === filter.key && !isMobile && (
                            <div className="front-filter__dropdown">
                                {filter.options.map((option) => (
                                    <label className="front-filter__option" key={option.value}>
                                        <input
                                            type="checkbox"
                                            checked={selectedFilters[filter.key]?.has(option.value)}
                                            onChange={() => onToggle(filter.key, option.value)}
                                        />
                                        <span className="front-filter__checkbox" aria-hidden="true">
                                            <i className="bi bi-check-lg"></i>
                                        </span>
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {isMobile && openKey &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className="front-filter__dropdown front-filter__dropdown--portal"
                        style={{
                            top: dropdownPos.top,
                            left: dropdownPos.left,
                            width: dropdownPos.width
                        }}
                    >
                        {visibleFilters.find((filter) => filter.key === openKey)?.options.map((option) => (
                            <label className="front-filter__option" key={option.value}>
                                <input
                                    type="checkbox"
                                    checked={selectedFilters[openKey]?.has(option.value)}
                                    onChange={() => onToggle(openKey, option.value)}
                                />
                                <span className="front-filter__checkbox" aria-hidden="true">
                                    <i className="bi bi-check-lg"></i>
                                </span>
                                <span>{option.label}</span>
                            </label>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    )
}

export default Filter
